import { type ActivityLabel } from '@jobilla/nexus-sdk'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { ArrowRightLeftIcon, CalendarIcon, FileTextIcon, type LucideIcon, MessageCircleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const activityLabelIcon: Record<ActivityLabel, LucideIcon> = {
	messaged: MessageCircleIcon,
	stage_moved: ArrowRightLeftIcon,
	applied: FileTextIcon,
	interview: CalendarIcon,
}

type ActivitySummaryProps = {
	label: ActivityLabel | null
	at: string | null
	actorName?: string | null
	campaignTitle?: string | null
}

/**
 * Renders a candidate's latest activity. The label and its grouping are decided by the
 * backend (ActivityLabel); this only maps the value to an icon and a localised string.
 */
export function ActivitySummary({ label, at, actorName, campaignTitle }: ActivitySummaryProps) {
	const { t } = useTranslation('common')

	if (!label) {
		return <span className="text-grey-400">{t('talent_pool.never_applied')}</span>
	}

	const Icon = activityLabelIcon[label]
	const meta = [
		at ? formatDistanceToNow(parseISO(at), { addSuffix: true }) : null,
		campaignTitle,
		actorName ? t('talent_pool.activity_by', { name: actorName }) : null,
	].filter(Boolean).join(' · ')

	return (
		<>
			<div className="inline-flex items-center gap-1.5 text-grey-700">
				<Icon className="size-3.5 text-grey-400" />
				<span>{t(`talent_pool.activity_label.${label}`)}</span>
			</div>
			{meta ? <div className="text-xs text-grey-500 mt-0.5">{meta}</div> : null}
		</>
	)
}
